import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { StaticPage } from '../../containers';
import {
  CTAButton,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  NamedLink,
  SearchListingCardV2,
} from '../../components';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { searchListings } from '../SearchPage/SearchPage.duck';
import PropTypes from 'prop-types';
import { stringify } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import LandingHeader from '../../components/LandingHeader/LandingHeader';

import css from './CityLandingPage.module.css';

const { LatLng, LatLngBounds } = sdkTypes;

const isValidLatitude = lat => lat >= -90 && lat <= 90;
const isValidLongitude = lng => lng >= -180 && lng <= 180;

const parseCoordinateList = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const coordinates = value
    .split(',')
    .map(part => Number.parseFloat(part.trim()))
    .filter(number => Number.isFinite(number));

  return coordinates.length > 0 ? coordinates : null;
};

export const parseCityOrigin = value => {
  const coordinates = parseCoordinateList(value);
  if (!coordinates || coordinates.length !== 2) {
    return null;
  }

  const [lat, lng] = coordinates;
  if (!isValidLatitude(lat) || !isValidLongitude(lng)) {
    return null;
  }

  return new LatLng(lat, lng);
};

export const parseCityBounds = value => {
  const coordinates = parseCoordinateList(value);
  if (!coordinates || coordinates.length !== 4) {
    return null;
  }

  const [neLat, neLng, swLat, swLng] = coordinates;
  if (
    !isValidLatitude(neLat) ||
    !isValidLongitude(neLng) ||
    !isValidLatitude(swLat) ||
    !isValidLongitude(swLng)
  ) {
    return null;
  }

  return new LatLngBounds(new LatLng(neLat, neLng), new LatLng(swLat, swLng));
};

export const createCitySearchParams = cityConfig => {
  const bounds = parseCityBounds(cityConfig.bounds);
  const origin = parseCityOrigin(cityConfig.origin);

  return {
    address: cityConfig.address,
    ...(bounds ? { bounds } : {}),
    ...(origin ? { origin } : {}),
  };
};

const resolveCityLabel = (intl, city, cityLabel) => {
  const translated = intl.formatMessage({
    id: `LandingPageCities.${cityLabel ? cityLabel : city.toLowerCase()}`,
  });

  return translated === `LandingPageCities.${cityLabel ? cityLabel : city.toLowerCase()}`
    ? city
    : translated;
};

const formatIntlMessage = (intl, id, fallback) => {
  const translated = intl.formatMessage({ id });
  return translated === id ? fallback : translated;
};

export const CityLandingPageComponent = props => {
  const { listings, intl, rootClassName, className, cityKey, cityConfig, cityLabel } = props;

  const classes = classNames(rootClassName || css.root, className);
  const citySearchParams = createCitySearchParams(cityConfig);
  const citySearchString = `?${stringify(citySearchParams)}`;
  const cityName = resolveCityLabel(intl, cityKey, cityLabel);
  const title = formatIntlMessage(
    intl,
    `LandingPage${cityKey}.seoTitle`,
    `Reserva Escaparates Pop Up en ${cityName} | ÛUPO`
  );
  const description = formatIntlMessage(
    intl,
    `LandingPage${cityKey}.seoDescription`,
    `Ven con tu Marca a un exclusivo Escaparate Pop Up en ${cityName} y Vende como un local`
  );
  const schemaTitle = formatIntlMessage(
    intl,
    `LandingPage${cityKey}.schemaTitle`,
    `Pop Up Stores ${cityName} | ÛUPO`
  );
  const schemaDescription = formatIntlMessage(
    intl,
    `LandingPage${cityKey}.schemaDescription`,
    `Reserva escaparates pop up por día en los lugares más fascinantes de ${cityName}`
  );
  const ctaTitle = formatIntlMessage(
    intl,
    `LandingPage${cityKey}.cta.title`,
    `¿Buscas el lugar perfecto para tu marca en ${cityName}?`
  );
  const zoneIndexes = [0, 1, 2, 3, 4, 5];
  const zoneItems = zoneIndexes
    .map(index => {
      const id = `LandingPage${cityKey}.zones.list.${index}`;
      const text = intl.formatMessage({ id });
      return text === id ? null : text;
    })
    .filter(v => v);

  return (
    <StaticPage
      className={classes}
      title={title}
      description={description}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
      </Helmet>

      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <LandingHeader
            currentPage={`LandingPage${cityKey}`}
            currentSearchParams={citySearchParams}
          />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.mainWrapper}>
          <section
            className={css.hero}
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(11, 21, 26, 0.86) 0%, rgba(11, 21, 26, 0.56) 48%, rgba(11, 21, 26, 0.26) 100%), url(${cityConfig.image})`,
            }}
          >
            <div className={css.heroInner}>
              <h1 className={css.heroTitle}>
                <span className={css.heroTitleLine}>
                  <FormattedMessage id={`LandingPage${cityKey}.titleLine1`} />
                </span>
                <span className={css.heroTitleLine}>
                  <FormattedMessage id={`LandingPage${cityKey}.titleLine2`} />
                </span>
              </h1>

              <p className={css.heroDescription}>
                <FormattedMessage id={`LandingPage${cityKey}.description`} />
              </p>

              <div className={css.heroActions}>
                <CTAButton
                  name="SearchPage"
                  to={{ search: citySearchString }}
                  className={css.heroPrimaryButton}
                >
                  <FormattedMessage id={`LandingPage${cityKey}.cta.button`} />
                </CTAButton>
                <CTAButton
                  name="LandingPageCities"
                  variant="secondary"
                  className={css.heroSecondaryButton}
                >
                  <FormattedMessage id="LandingUuper.viewAllCitiesButton" />
                </CTAButton>
              </div>
            </div>
          </section>

          <section className={css.listingsSection}>
            <div className={css.sectionHeading}>
              <h2 className={css.sectionTitle}>{ctaTitle}</h2>
              <p className={css.sectionDescription}>{schemaDescription}</p>
            </div>

            {listings && listings.length > 0 ? (
              <div className={css.listingsGrid}>
                {listings.slice(0, 6).map(listing => (
                  <SearchListingCardV2
                    key={listing.id.uuid}
                    listing={listing}
                    renderSizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw"
                  />
                ))}
              </div>
            ) : (
              <div className={css.emptyState}>
                <FormattedMessage id="SectionCityListings.notFound" values={{ city: cityName }} />
              </div>
            )}

            <div className={css.ctaRow}>
              <CTAButton
                name="SearchPage"
                to={{ search: citySearchString }}
                className={css.sectionButton}
              >
                <FormattedMessage id={`LandingPage${cityKey}.cta.button`} />
              </CTAButton>
            </div>
          </section>

          <section className={css.seoSection}>
            <div className={css.seoCard}>
              <h2 className={css.seoTitle}>
                <FormattedMessage id={`LandingPage${cityKey}.zones.title`} />
              </h2>

              <div className={css.zoneList}>
                {zoneItems.map(zone => (
                  <span key={zone} className={css.zoneItem}>
                    {zone}
                  </span>
                ))}
              </div>

              <p className={css.seoAfter}>
                <FormattedMessage id={`LandingPage${cityKey}.zones.after`} />
              </p>
            </div>
          </section>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

CityLandingPageComponent.propTypes = {
  cityKey: PropTypes.string.isRequired,
  cityConfig: PropTypes.shape({
    path: PropTypes.string.isRequired,
    routeName: PropTypes.string.isRequired,
    bounds: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    searchParams: PropTypes.object.isRequired,
  }).isRequired,
  listings: PropTypes.array,
  intl: intlShape.isRequired,
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  cityLabel: PropTypes.string,
};

CityLandingPageComponent.defaultProps = {
  rootClassName: null,
  className: null,
  cityLabel: null,
  listings: [],
};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.SearchPage;
  const listings = currentPageResultIds ? getListingsById(state, currentPageResultIds) : [];
  return { listings };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadCityListings: () => {
    const citySearchParams = createCitySearchParams(ownProps.cityConfig);

    return dispatch(
      searchListings({
        ...citySearchParams,
        perPage: 100,
        include: ['author', 'author.stripeAccount', 'images'],
        'fields.listing': ['title', 'price', 'publicData'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'stripeAccount'],
        'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        'limit.images': 8,
      })
    );
  },
});

class CityLandingPageWrapper extends React.Component {
  componentDidMount() {
    this.props.loadCityListings();
  }

  render() {
    return <CityLandingPageComponent {...this.props} />;
  }
}

const CityLandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CityLandingPageWrapper);

export default CityLandingPage;
